import {
  trackNodeOrientationDegrees,
  type CoasterProject,
  type CoasterProjectV1,
  type TrackBehavior,
} from '../schema/index.ts';

type RideProject = CoasterProject | CoasterProjectV1;
import type {
  BankKey,
  BSplineSpan,
  SemanticSection,
  TableTrackSampler,
  TrackCompiler,
  TrackDefinition,
} from '../track/index.ts';
import {
  CubicBSpline,
  length,
  normalize,
  projectPerpendicular,
  rotateAroundAxis,
  signedAngle,
} from '../track/index.ts';
import type { CoasterFamilyDefinition } from '../physics/index.ts';
import { buildFamilyPhysicsDefinition } from '../ride/family-registry.ts';
import { materializeAuthoredProject } from '../builder-v2/materialize.ts';

export interface ProjectTrackSpan {
  readonly index: number;
  readonly uStart: number;
  readonly uEnd: number;
  readonly startDistance: number;
  readonly endDistance: number;
  readonly controlPointIndices: readonly number[];
}

export interface CompiledProjectTrack {
  readonly sampler: TableTrackSampler;
  readonly spans: readonly ProjectTrackSpan[];
  readonly changedSpanIndices: readonly number[];
}

export interface StationTrainLayout {
  readonly startDistance: number;
  readonly endDistance: number;
  readonly lengthMeters: number;
  readonly requiredLengthMeters: number;
  readonly frontClearanceMeters: number;
  readonly parkDistance: number;
  readonly fits: boolean;
}

function behaviorToSection(
  behavior: TrackBehavior,
  id: string,
  startDistance: number,
  endDistance: number,
): SemanticSection {
  if (behavior.kind === 'lift') {
    return {
      id,
      type: 'lift',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.targetSpeedMps,
        maxAcceleration: 12,
        gain: 6,
        antiRollback: true,
      },
    };
  }
  if (behavior.kind === 'launch') {
    return {
      id,
      type: 'launch',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.targetSpeedMps,
        maxAcceleration: behavior.accelerationMps2,
      },
    };
  }
  if (behavior.kind === 'brake') {
    return {
      id,
      type: 'brake',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.targetSpeedMps,
        maxDeceleration: behavior.decelerationMps2,
      },
    };
  }
  if (behavior.kind === 'station') {
    return {
      id,
      type: 'station',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.dispatchSpeedMps,
        maxAcceleration: 12,
        maxDeceleration: 4,
        holdSeconds: behavior.holdSeconds,
      },
    };
  }
  if (behavior.kind === 'photo') return { id, type: 'photo', startDistance, endDistance };
  return { id, type: 'normal', startDistance, endDistance };
}

export function distanceAtParameter(sampler: TableTrackSampler, targetU: number): number {
  const { u, distance } = sampler.table;
  const target = Math.min(1, Math.max(0, targetU));
  let low = 0;
  let high = u.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if (u[middle]! <= target) low = middle;
    else high = middle;
  }
  const span = u[high]! - u[low]!;
  const fraction = span > 1e-9 ? (target - u[low]!) / span : 0;
  return distance[low]! * (1 - fraction) + distance[high]! * fraction;
}

function unwrapRadians(value: number, around: number): number {
  let result = value;
  while (result - around > Math.PI) result -= Math.PI * 2;
  while (result - around < -Math.PI) result += Math.PI * 2;
  return result;
}

function equivalentRadiansNearest(value: number, around: number): number {
  return unwrapRadians(value, around);
}

function authoredFrameCorrections(
  project: CoasterProject,
  sampler: TableTrackSampler,
  sectionCount: number,
): readonly { readonly nodeIndex: number; readonly correction: number }[] {
  if (project.track.geometryKind !== 'authored_grid_v2') return [];

  const firstPiece = project.track.pieces[0];
  if (!firstPiece) return [];
  const boundaryStates = [
    {
      nodeIndex: 0,
      pose: firstPiece.entryPose,
      orientationDegrees:
        (firstPiece.spans[0]?.bankStartDegrees ?? 0) +
        (firstPiece.spans[0]?.windingStartDegrees ?? 0),
    },
  ];
  let accumulatedSections = 0;
  for (const piece of project.track.pieces) {
    accumulatedSections += piece.sectionIds.length;
    const finalSpan = piece.spans.at(-1);
    boundaryStates.push({
      nodeIndex: accumulatedSections,
      pose: piece.exitPose,
      orientationDegrees: (finalSpan?.bankEndDegrees ?? 0) + (finalSpan?.windingEndDegrees ?? 0),
    });
  }

  const corrections: { nodeIndex: number; correction: number }[] = [];
  let previousCorrection = 0;
  for (const boundary of boundaryStates) {
    const { nodeIndex, pose, orientationDegrees } = boundary;
    const parameter = sectionCount > 0 ? nodeIndex / sectionCount : 0;
    const parameterDistance = distanceAtParameter(sampler, parameter);
    // Sampling a closed table at its exact length wraps to distance zero. The
    // final authored boundary needs the frame immediately before the seam,
    // otherwise its correction is calculated from the station's starting
    // frame and a fake roll is distributed through the closing section.
    const distance =
      nodeIndex === sectionCount
        ? Math.max(0, sampler.length - Math.min(1e-5, sampler.length * 1e-7))
        : parameterDistance;
    const sample = sampler.sampleByDistance(distance);
    const worldVertical =
      pose.orientation === 'inverted' ? { x: 0, y: -1, z: 0 } : { x: 0, y: 1, z: 0 };
    const projectedWorldUp = projectPerpendicular(worldVertical, sample.tangent);
    const semanticBankRadians =
      pose.bank === 'left' ? -Math.PI / 4 : pose.bank === 'right' ? Math.PI / 4 : 0;
    const intendedPhysicalUp =
      length(projectedWorldUp) < 1e-5
        ? null
        : rotateAroundAxis(normalize(projectedWorldUp), sample.tangent, semanticBankRadians);
    // Corrections align the rotation-minimizing frame before the authored
    // bank/winding key is applied. This distinction matters for planar half
    // loops: their centerline transport supplies the 180-degree inversion,
    // while half rolls supply it explicitly through winding. Both must arrive
    // at the same physical inverted connector without an extra corrective twist.
    const correctionTarget = intendedPhysicalUp
      ? rotateAroundAxis(intendedPhysicalUp, sample.tangent, (-orientationDegrees * Math.PI) / 180)
      : null;
    const correction = !correctionTarget
      ? previousCorrection
      : unwrapRadians(
          signedAngle(sample.rmfUp, correctionTarget, sample.tangent),
          previousCorrection,
        );
    corrections.push({ nodeIndex, correction });
    previousCorrection = correction;
  }
  return corrections;
}

function correctionAtNode(
  nodeIndex: number,
  corrections: readonly { readonly nodeIndex: number; readonly correction: number }[],
): number {
  if (!corrections.length) return 0;
  const exact = corrections.find((entry) => entry.nodeIndex === nodeIndex);
  if (exact) return exact.correction;

  let before = corrections[0]!;
  let after = corrections.at(-1)!;
  for (let index = 1; index < corrections.length; index += 1) {
    if (corrections[index]!.nodeIndex >= nodeIndex) {
      after = corrections[index]!;
      before = corrections[index - 1]!;
      break;
    }
  }
  const span = after.nodeIndex - before.nodeIndex;
  if (span <= 0) return after.correction;
  const progress = (nodeIndex - before.nodeIndex) / span;
  return before.correction + (after.correction - before.correction) * progress;
}

function changedSpans(
  spans: readonly BSplineSpan[],
  changedControlPointIndices: readonly number[],
): number[] {
  if (!changedControlPointIndices.length) return spans.map((span) => span.index);
  const changed = new Set(changedControlPointIndices);
  return spans
    .filter((span) => span.controlPointIndices.some((index) => changed.has(index)))
    .map((span) => span.index);
}

export function compileProjectTrackResult(
  project: RideProject,
  compiler: TrackCompiler,
  changedControlPointIndices: readonly number[] = [],
): CompiledProjectTrack {
  const sourceProject =
    'geometryKind' in project.track && project.track.geometryKind === 'authored_grid_v2'
      ? materializeAuthoredProject(project as CoasterProject)
      : project;
  const modular =
    'geometryKind' in sourceProject.track &&
    (sourceProject.track.geometryKind === 'modular_piecewise_bezier' ||
      sourceProject.track.geometryKind === 'authored_grid_v2');
  const base: TrackDefinition = {
    geometryKind: modular ? 'piecewise_bezier' : 'cubic_bspline',
    controlPoints: sourceProject.track.nodes.map((node) => ({ ...node.position })),
    ...(modular
      ? {
          bezierSegments: sourceProject.track.sections.map((section, index) => {
            if (!('geometry' in section) || !section.geometry)
              throw new Error(`Modular section ${section.id} has no geometry.`);
            return {
              start: { ...sourceProject.track.nodes[index]!.position },
              control1: { ...section.geometry.control1 },
              control2: { ...section.geometry.control2 },
              end: {
                ...sourceProject.track.nodes[
                  sourceProject.track.closed
                    ? (index + 1) % sourceProject.track.nodes.length
                    : index + 1
                ]!.position,
              },
            };
          }),
        }
      : {}),
    closed: sourceProject.track.closed,
  };
  const curve = modular ? null : new CubicBSpline(base);
  const baseSampler = compiler.compile(base, undefined, changedControlPointIndices);
  const sectionCount = Math.max(1, sourceProject.track.sections.length);
  const sections = sourceProject.track.sections.map((_, geometricIndex) => {
    // A periodic cubic B-spline span runs nearest node i+1 -> i+2 even
    // though its four control points begin at i. Rotate authored semantics
    // once so a node i -> i+1 behavior is attached to the matching geometry.
    const authoredIndex =
      sourceProject.track.closed && !modular
        ? (geometricIndex + 1) % sourceProject.track.sections.length
        : geometricIndex;
    const section = sourceProject.track.sections[authoredIndex]!;
    return behaviorToSection(
      section.behavior,
      section.id,
      distanceAtParameter(baseSampler, geometricIndex / sectionCount),
      distanceAtParameter(baseSampler, (geometricIndex + 1) / sectionCount),
    );
  });
  const nodeCount = Math.max(1, sourceProject.track.nodes.length);
  const authoredCorrections =
    'geometryKind' in sourceProject.track && sourceProject.track.geometryKind === 'authored_grid_v2'
      ? authoredFrameCorrections(sourceProject as CoasterProject, baseSampler, sectionCount)
      : [];
  const bank: BankKey[] = sourceProject.track.nodes.map((_, geometricIndex) => {
    const authoredIndex =
      sourceProject.track.closed && !modular
        ? (geometricIndex + 1) % sourceProject.track.nodes.length
        : geometricIndex;
    return {
      distance: distanceAtParameter(
        baseSampler,
        geometricIndex / (modular ? sectionCount : nodeCount),
      ),
      value:
        (trackNodeOrientationDegrees(sourceProject.track.nodes[authoredIndex]!) * Math.PI) / 180 +
        correctionAtNode(geometricIndex, authoredCorrections),
    };
  });
  if (sourceProject.track.closed && bank.length) {
    // Authored inversions intentionally keep winding unwrapped (360°, 720°,
    // and so on). The station's first node is stored as 0°, which is the same
    // physical orientation. Closing with the literal 0° would interpolate a
    // fake full roll through the final section. Re-express the seam using the
    // nearest equivalent angle while preserving every authored inversion. Use
    // the correction at the duplicated final boundary rather than reusing the
    // first sample's correction: a closed RMF can require a different
    // unwrapped correction after one complete circuit.
    const seamNodeIndex = modular ? 0 : 1 % sourceProject.track.nodes.length;
    const authoredTrack =
      'geometryKind' in sourceProject.track &&
      sourceProject.track.geometryKind === 'authored_grid_v2'
        ? sourceProject.track
        : null;
    const authoredFinalPieceId = authoredTrack?.authoredTopology.components[0]?.pieceIds.at(-1);
    const authoredFinalSpan = authoredTrack?.pieces
      .find((piece) => piece.id === authoredFinalPieceId)
      ?.spans.at(-1);
    // The duplicated authored boundary belongs to the final piece, not to the
    // station's stored zero-winding node. A half roll followed by a planar
    // half loop can be visibly upright while retaining an unwrapped 180-degree
    // render history. Pair that final authored winding with its final frame
    // correction so the two cancel physically instead of twisting the closing
    // section to meet the station's unrelated zero-degree representation.
    const seamOrientationDegrees = authoredFinalSpan
      ? (authoredFinalSpan.bankEndDegrees ?? 0) + (authoredFinalSpan.windingEndDegrees ?? 0)
      : trackNodeOrientationDegrees(sourceProject.track.nodes[seamNodeIndex]!);
    const seamValue =
      (seamOrientationDegrees * Math.PI) / 180 +
      correctionAtNode(sectionCount, authoredCorrections);
    bank.push({
      distance: baseSampler.length,
      value: equivalentRadiansNearest(seamValue, bank.at(-1)!.value),
    });
  }
  const sampler = compiler.compile({ ...base, sections, bank });
  const spans = modular
    ? sourceProject.track.sections.map((_, index) => ({
        index,
        uStart: index / sectionCount,
        uEnd: (index + 1) / sectionCount,
        controlPointIndices: [
          index,
          sourceProject.track.closed
            ? (index + 1) % sourceProject.track.nodes.length
            : Math.min(index + 1, sourceProject.track.nodes.length - 1),
        ],
      }))
    : curve!.spans;
  return {
    sampler,
    spans: spans.map((span) => ({
      ...span,
      startDistance: distanceAtParameter(sampler, span.uStart),
      endDistance: distanceAtParameter(sampler, span.uEnd),
    })),
    changedSpanIndices: changedSpans(spans, changedControlPointIndices),
  };
}

export function compileProjectTrack(
  project: RideProject,
  compiler: TrackCompiler,
  changedControlPointIndices: readonly number[] = [],
): TableTrackSampler {
  return compileProjectTrackResult(project, compiler, changedControlPointIndices).sampler;
}

export function coasterFamilyForProject(project: RideProject): CoasterFamilyDefinition {
  return buildFamilyPhysicsDefinition(project as CoasterProject);
}

export function stationTrainLayout(
  project: RideProject,
  sampler: TableTrackSampler,
): StationTrainLayout | null {
  const sections = [...sampler.table.sections].sort(
    (left, right) => left.startDistance - right.startDistance,
  );
  if (!sections.length) return null;
  const stationStartIndex = sections.findIndex((section, index) => {
    if (section.type !== 'station') return false;
    const previous = sections[(index - 1 + sections.length) % sections.length];
    return previous?.type !== 'station';
  });
  const firstStationIndex =
    stationStartIndex >= 0 ? stationStartIndex : sections[0]?.type === 'station' ? 0 : -1;
  if (firstStationIndex < 0) return null;

  const startDistance = sections[firstStationIndex]!.startDistance;
  let lengthMeters = 0;
  for (let offset = 0; offset < sections.length; offset += 1) {
    const section = sections[(firstStationIndex + offset) % sections.length]!;
    if (section.type !== 'station') break;
    lengthMeters += Math.max(0, section.endDistance - section.startDistance);
  }
  if (!(lengthMeters > 0)) return null;

  const spacing = project.train.carSpacingMeters;
  const frontClearanceMeters = spacing * 0.5;
  const requiredLengthMeters = project.train.carCount * spacing;
  const unwrappedEnd = startDistance + lengthMeters;
  const rawParkDistance = unwrappedEnd - frontClearanceMeters;
  const normalizeDistance = (distance: number) =>
    sampler.closed
      ? ((distance % sampler.length) + sampler.length) % sampler.length
      : Math.max(0, Math.min(sampler.length, distance));
  return {
    startDistance,
    endDistance: normalizeDistance(unwrappedEnd),
    lengthMeters,
    requiredLengthMeters,
    frontClearanceMeters,
    parkDistance: normalizeDistance(rawParkDistance),
    fits: lengthMeters + 1e-6 >= requiredLengthMeters,
  };
}

export function initialTrainReferenceDistance(
  project: RideProject,
  sampler?: TableTrackSampler,
): number {
  const fallback = Math.max(0.1, (project.train.carCount - 0.5) * project.train.carSpacingMeters);
  if (!sampler) return fallback;
  return stationTrainLayout(project, sampler)?.parkDistance ?? fallback;
}
